import { useEffect, useRef } from "react";
import type { AiConnection, ExportState, TripsHeard } from "../../src/web/types.ts";
import { keepOpen } from "./event-stream.ts";

// Live updates about me, across trips (GET /api/me/events, server-sent events): what changed about me,
// with what it is now. Every (re)connect starts with all of it, so nothing that happened while the
// page loaded, or while the connection was down, is missed. Each kind of event has its handler:
// `ais`, my AIs (getting started waits for the first); `trips`, my trips as they are now, with my
// waiting Trip Passes and plan (Your trips loads them again when it's behind); `active`, one of my AIs
// calling Tripmo this moment (the wait for a first trip says it's working on it); `data`, my Download your
// data (the profile). One stream per page
// that listens.
export interface PersonEvents {
  ais?: (ais: AiConnection[]) => void;
  trips?: (heard: TripsHeard) => void;
  active?: () => void;
  // My Download your data: being prepared, ready, gone (the profile shows it as it changes).
  data?: (state: ExportState) => void;
}

export function usePersonEvents(on: PersonEvents, listening = true) {
  // The latest handlers, without opening a new stream when they change.
  const handlers = useRef(on);
  useEffect(() => {
    handlers.current = on;
  });
  useEffect(() => {
    if (!listening) return;
    // Reconnecting (event-stream.ts), the server sends everything again.
    return keepOpen(
      () => "/api/me/events",
      (source) => {
        source.addEventListener("ais", (e) => {
          const { ais } = JSON.parse((e as MessageEvent<string>).data) as { ais: AiConnection[] };
          handlers.current.ais?.(ais);
        });
        source.addEventListener("trips", (e) => {
          handlers.current.trips?.(JSON.parse((e as MessageEvent<string>).data) as TripsHeard);
        });
        source.addEventListener("active", () => handlers.current.active?.());
        source.addEventListener("data", (e) => {
          handlers.current.data?.(JSON.parse((e as MessageEvent<string>).data) as ExportState);
        });
      },
    );
  }, [listening]);
}
